import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { StorageKey, StorageService } from '@core/services/storage/storage.service';
import { NgxLoadingBar } from '@ngx-loading-bar/core';
import { TranslateService } from '@ngx-translate/core';
import {
  PRINTER_DRIVER_LOGOUT,
  PRINTER_DRIVER_ORIGIN,
  PrinterQueryParam,
} from '@shared/model/vitual-printer-driver.type';
import { AnnouncementsStore } from '@features/configgeneral/announcements/services/announcements-store.service';
import { PrimeNG } from 'primeng/config';
import { Observable, filter, map, switchMap, takeUntil, tap } from 'rxjs';
import { BaseComponent } from './core/components/base-component';
import { FooterComponent } from './core/components/footer/footer.component';
import { HeaderComponent } from './core/components/header/header.component';
import { MainComponent } from './core/components/main/main.component';
import { MenuComponent } from './core/components/menu/menu.component';
import { AppRoutes } from './core/routes/route.paths';
import { AuthentificationService } from './core/services/authentification/authentification.service';
import { InactivityService } from './core/services/inactivity/inactivity.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [HeaderComponent, MenuComponent, FooterComponent, NgxLoadingBar, AsyncPipe, MainComponent],
})
export class AppComponent extends BaseComponent {
  private readonly config = inject(PrimeNG);
  private readonly router = inject(Router);
  private readonly authentificationService = inject(AuthentificationService);
  private readonly translate = inject(TranslateService);
  private readonly announcementsStore = inject(AnnouncementsStore);
  protected showSideMenu$: Observable<boolean>;

  constructor() {
    super();

    inject(InactivityService);

    this.showSideMenu$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap(() => window.scrollTo(0, 0)),
      map(() => this.authentificationService.isLoggedIn() && !this.router.url.includes(AppRoutes.Welcome)),
      takeUntil(this.destroy$),
    );

    this.translate.onLangChange
      .pipe(
        switchMap(() => this.translate.get('primeng')),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => this.config.setTranslation(res));

    // Annonces : faute d'événement de connexion, on s'appuie sur la navigation (load() est idempotent).
    // Le logout ne recharge pas la page : reset() évite que l'utilisateur suivant voie celles du précédent.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.authentificationService.isLoggedIn()) {
          this.announcementsStore.load();
        } else {
          this.announcementsStore.reset();
        }
      });

    // For PB Printer C# app
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get(PrinterQueryParam.Origin) === PRINTER_DRIVER_ORIGIN) {
      // If logout action
      if (urlParams.get(PrinterQueryParam.Logout) === 'true') {
        this.authentificationService.logout();
        AuthentificationService.sendMessageToWebview(`${PRINTER_DRIVER_LOGOUT}|true`);
      } else {
        StorageService.set(StorageKey.AuthOrigin, PRINTER_DRIVER_ORIGIN);
        this.router.navigateByUrl(this.appRoutes.Welcome);
      }
    }
    // End C#
  }
}
